import { useState } from 'react';
import { Box, Typography, Button, Grid, Alert, Chip, Paper } from '@mui/material';
import { Add, Refresh, Assessment, Info } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDataProvider } from 'react-admin';
import { useWorkflowList, WorkflowCard, WorkflowFilters } from '@/components/workflow';
import type { WorkflowInstance } from '@/components/workflow';

export const EmploymentEquityWorkflowManager = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [filters, setFilters] = useState<{ status?: string[]; workflowType?: string[]; search?: string }>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { workflows, total, isLoading, error, refetch } = useWorkflowList({
    dataProvider,
    resource: 'workflow-instances',
    filters,
    perPage: 50,
  });

  const eeWorkflows = workflows.filter(w =>
    w.workflowType === 'ee-report' || w.workflowType === 'training'
  );

  const activeCount = eeWorkflows.filter(w => w.status === 'active').length;
  const pausedCount = eeWorkflows.filter(w => w.status === 'paused').length;
  const completedCount = eeWorkflows.filter(w => w.status === 'completed' || w.status === 'cancelled').length;

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    refetch();
  };

  const handleWorkflowOpen = (workflow: WorkflowInstance) => {
    navigate('/wizard/ee-report');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Employment Equity Workflows
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage employment equity reporting processes
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }} icon={<Info />}>
        EE reporting is typically done annually. Generate a new report to start a workflow.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #1976d2' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Active
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {activeCount}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #ed6c02' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Paused
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#ed6c02' }}>
              {pausedCount}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, borderTop: '4px solid #2e7d32' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
              Completed
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              {completedCount}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/wizard/ee-report')}
        >
          Submit EE Report
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <WorkflowFilters />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Typography color="text.secondary">Loading workflows...</Typography>
      ) : eeWorkflows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Assessment sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No EE Workflows Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Employment equity reporting is typically done annually. No active EE workflows found.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/wizard/ee-report')}
            >
              Submit EE Report
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/ee-profiles')}
            >
              View EE Profiles
            </Button>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {eeWorkflows.map(workflow => (
            <Grid key={workflow.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <WorkflowCard
                workflow={workflow}
                onOpen={handleWorkflowOpen}
                showActions={false}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Total: {eeWorkflows.length} workflow(s)
        </Typography>
        <Chip
          icon={<Assessment />}
          label="Employment Equity"
          size="small"
          variant="outlined"
        />
      </Box>
    </Box>
  );
};